import { Box, Button, Container, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Bell, ChevronRight, CreditCard, FileText, Shield, SunMoon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import AppHeader from '@/components/navigation/AppHeader';
import { hasSeenAppearanceBadge } from '@/lib/appearance/badgeSeen';
import { settingsCopy } from '@/copy';

interface SettingsRow {
  to: '/settings/notifications' | '/settings/appearance' | '/blocked-users' | '/reports' | '/settings/subscription';
  icon: LucideIcon;
  title: string;
  description: string;
  isNew?: boolean;
}

/**
 * Parent /settings page — a plain list of rows linking to the existing child
 * pages (which keep their current paths). Reached from the Menu's single
 * "Settings" row. Back mirrors the child settings pages (`router.history.back()`).
 */
export function Settings() {
  const router = useRouter();
  const navigate = useNavigate();
  const { rows } = settingsCopy;
  const showAppearanceBadge = !hasSeenAppearanceBadge();

  const settingsRows: SettingsRow[] = [
    { to: '/settings/notifications', icon: Bell, ...rows.notifications },
    { to: '/settings/appearance', icon: SunMoon, ...rows.appearance, isNew: showAppearanceBadge },
    { to: '/blocked-users', icon: Shield, ...rows.blockedMembers },
    { to: '/reports', icon: FileText, ...rows.reports },
    { to: '/settings/subscription', icon: CreditCard, ...rows.subscription },
  ];

  return (
    <PageWrapper>
      <SEO title={settingsCopy.pageTitle} />
      <AppHeader page="settings" />
      <Box minH="100vh">
        <Container maxW="container.md" py={6}>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="center">
              <Button
                leftIcon={<ArrowLeft size={18} />}
                variant="ghost"
                onClick={() => router.history.back()}
                size={{ base: 'sm', lg: 'md' }}
                color="var(--canvas-text)"
                _hover={{ bg: 'var(--color-white-overlay-10)' }}
                _active={{ bg: 'var(--color-white-overlay-20)' }}
              >
                Back
              </Button>
              <Heading as="h1" id="settings-heading" size="lg" color="var(--canvas-text)">
                {settingsCopy.pageTitle}
              </Heading>
              <Box w="60px" />
            </HStack>

            <VStack
              as="ul"
              role="list"
              listStyleType="none"
              spacing={3}
              align="stretch"
              m={0}
              p={0}
              aria-labelledby="settings-heading"
            >
              {settingsRows.map(({ to, icon: Icon, title, description, isNew }) => (
                <Box as="li" key={to}>
                  <Box
                    as="button"
                    type="button"
                    onClick={() => navigate({ to })}
                    w="full"
                    minH="44px"
                    p={4}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    textAlign="left"
                    borderRadius="var(--r-card)"
                    bg="var(--card-bg)"
                    // PageWrapper sets a light text color; without an explicit card
                    // text color the icon/chevron (currentColor) vanish in Light mode.
                    color="var(--card-text)"
                    border="var(--card-border)"
                    boxShadow="var(--card-shadow)"
                    _hover={{ bg: 'var(--card-surface-2)' }}
                    _focusVisible={{ boxShadow: '0 0 0 2px var(--color-primary-purple)' }}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <VStack align="start" spacing={0} flex="1">
                      <HStack spacing={2}>
                        <Text fontWeight="semibold" color="var(--card-text)">
                          {title}
                        </Text>
                        {isNew && (
                          <Box
                            as="span"
                            px={2}
                            py="1px"
                            borderRadius="full"
                            // Fixed brand-purple pill with white text (5.70:1, same in both
                            // modes; --badge-new-* in variables.css). The old purple-on-purple-tint
                            // was 2.35:1 on dark cards.
                            bg="var(--badge-new-bg)"
                            color="var(--badge-new-text)"
                            fontSize="xs"
                            fontWeight={600}
                          >
                            {settingsCopy.newBadge}
                          </Box>
                        )}
                      </HStack>
                      <Text fontSize="sm" color="var(--card-text-2)">
                        {description}
                      </Text>
                    </VStack>
                    <ChevronRight size={18} aria-hidden="true" />
                  </Box>
                </Box>
              ))}
            </VStack>
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
}
